// AppShell — the calm three-zone frame, pure.
//
// The centrepiece of the design vision (.claude/research/design/02): one collapsible
// left NAV zone, a fixed dominant CENTRE (a PURE EDITOR — toolbar + material,
// no foot), and a single summonable RIGHT zone that is empty by default. The
// Assistant is NOT a shell zone — it is the genie FLOAT, a fixed overlay the
// HOST renders over the frame (chat-composer-states.md), so it occupies no grid
// track and the editor keeps max canvas. AppShell owns only the FRAME GEOMETRY
// and how it reshapes per mode — it renders none of the furniture itself; every
// piece (NavRail, MaterialToolbar, the material, SecondaryPane) arrives as a
// ReactNode slot, composed by the caller. That keeps AppShell a pure layout
// orchestrator with zero domain knowledge and no component imports of its own.
//
// The load-bearing invariant (.claude/research/design/02 "modes"): the material never
// leaves the centre. The frame's ONLY reshape is filling or emptying the right
// zone — nothing teleports.
//
// Which secondary is open is the frame's single control axis, so it is modelled
// as ONE value: `openSecondary` (a `SecondarySlot`, or `null` for Focus). There
// is no second mode-switch mirroring it and no bijection folding one into the
// other — the open slot IS the state (see AppShellHost / DockRibbon).
//
// Zone state is CONTROLLED (props), never internal surprise state:
// `openSecondary` and `navState` are inputs, and every user intent to change
// them is reported UP (via the furniture's own callbacks) for the shell to
// fold. The only responsive behaviour AppShell owns is expressed in CSS
// (breakpoints in AppShell.css): the right zone becomes an overlay drawer +
// scrim below ~832px; its scrim tap reports up via `onSecondaryScrimClick`.
// The left menu never lies over the page: open or folded it takes its own
// room and pushes the centre at every width; only `hidden` tucks it away.

import { exhaustSwitchCase } from "@bilda/utils";
import type { CSSProperties, ReactNode } from "react";
import "./AppShell.css";

// Which secondary the right zone holds when open. Exactly ONE at a time
// (.claude/research/design/02: "never two at once"); the set is closed, so adding a
// slot breaks every match that folds over it. "versions" was removed —
// it returns when a versions surface (compare/combine) is designed.
// "conversation" was removed (subtractive audit 2026-08-19): chat has two homes
// — the float and its own splittable tab — so the vestigial third home (a dock
// slot) went, and the dock now means exactly "this material's review" (Review +
// Quality), which is what the DockRibbon already advertises.
// "situation-profile" was removed (chat integration Slice 1): the workspace
// audience ("who this is for") had TWO competing homes — this dock slot's
// SituationProfile panel, and the AudienceBar chat banner. Both collapsed into
// ONE `SituationProfileChip` riding the chat banner slot, so the dock no
// longer carries an audience surface at all.
// Right panel v2 (2026-10-06): `panel` is the chat's one panel (the material
// made here under its two lenses, the sources and where they are used, other
// files); it replaced the four F65/cite-B tabs (fact-check · pedagogy · sources
// · files) and is the chrome ribbon's only slot. `review` (ProposalReview) +
// `grounding` (the terse QualitySummary glance) remain for the contextual jumps
// (go-to-change / issues chip) that still summon them.
// Redesign step 1: `assistant` is the chat itself, docked here beside the main
// view; `panel` is its Context sibling (the chat panel's second tab).
export type SecondarySlot = "assistant" | "review" | "grounding" | "panel";

// Whether a slot applies to the ACTIVE view — a named sum, never a bare
// boolean-plus-string pair. The reason is the author-facing tooltip copy the
// palette shows when a slot is not offered. Owned here (not the host) because
// both the shell's summon group/accelerators AND OmnibarHost's panel-command
// rows gate on it — the shared vocabulary for "can this slot be summoned right
// now," alongside the `SecondarySlot` it's indexed by.
export type SlotAvailable = { readonly type: "available" };
export type SlotUnavailable = {
    readonly type: "unavailable";
    readonly reason: string;
};
export type SlotAvailability = SlotAvailable | SlotUnavailable;

// The frame's open secondary, or `null` for Focus (nothing summoned, the
// material keeps the full width). This single value is the ONE source of truth
// for the right zone — there is no separate mode switch to keep in sync.
export type OpenSecondary = SecondarySlot | null;

// The right dock as ONE derived fact — the single control value the panel body,
// the dock ribbon, and the resize handle all project from, so "handle without
// panel" is unrepresentable at this boundary (ui-robustness.md §3.1: the stuck
// handle was two conditionals for one state). Three named states, not scattered
// booleans, because the dock is EDITOR-scoped and has two live shapes within the
// editor:
//   gone      — not a material editor: no ribbon, no body, no handle.
//   collapsed — editor at Focus (no slot summoned): the ribbon + mounted (but
//               CSS-collapsed) body show; the handle is also mounted (so it can
//               CSS-transition in step with the body's own open animation) but
//               stays functionally absent — visibility:hidden, not focusable —
//               so there is still no INTERACTIVE handle without an open panel.
//   open{slot} — editor holding one summoned slot: ribbon + expanded panel +
//               an interactive handle glued to the panel's animating edge.
export type DockGone = { readonly type: "gone" };
export type DockCollapsed = { readonly type: "collapsed" };
export type DockOpen = { readonly type: "open"; readonly slot: SecondarySlot };
export type DockState = DockGone | DockCollapsed | DockOpen;

// The nav rail's display state (.claude/research/design/02's shell state ×
// the responsive collapse ladder). A named sum, not two booleans:
//   expanded  — full rail with labels (~240px)
//   collapsed — thin icon rail (~48px)
//   hidden    — off-canvas (mobile; summoned as an overlay)
export type NavRailState = "expanded" | "collapsed" | "hidden";

// The centre's surface register — the ONE fact the ambient-vs-paper background
// swap reads (workspace-landing sketch's single idea). A named union, never a
// boolean, so the swap is legible and a third register could be added without
// re-plumbing:
//   landing — the pinned home tab (the WorkspaceHome overview). The centre
//     shares the top-strip + rail's AMBIENT ground (--color-bg-subtle) end to
//     end, so the chrome "melts" into one continuous field.
//   paper — any other focused tab (a resource, chat, settings, …): a focused
//     work surface. Both now share the warm ground (D31); the CSS no
//     longer paints paper, so the swap is visually inert.
// The sketch's PaperSurface also carried the open resource's `title`; the live
// shell drops it — the ResourceTabs strip is the real top-bar chip and already
// renders every tab's title, so a second copy here would be redundant payload.
export type LandingSurface = { readonly type: "landing" };
export type PaperSurface = { readonly type: "paper" };
export type WorkspaceSurface = LandingSurface | PaperSurface;

export type AppShellProps = {
    // The right dock as ONE fact (gone | collapsed | open+slot). Gates ALL three
    // dock parts — the panel body, the ribbon, and the resize handle — so they
    // cannot disagree (no handle without a panel). Drives the summoned state and
    // which slot the right zone holds; nothing else moves.
    readonly dock: DockState;
    // The left rail's display state (controlled; the shell owns the toggle).
    readonly navState: NavRailState;
    // The centre's surface register (landing | paper). Drives ONLY the
    // background-token swap (AppShell.css reads `data-surface`); no geometry
    // moves — the material never leaves the centre.
    readonly surface: WorkspaceSurface;

    // === Slots — the furniture, composed by the caller ===
    // The single horizontal chrome band across the very top (minimal-chrome
    // §3): window controls, the health corner. Optional
    // — when absent the shell is just the three-zone grid (existing stories).
    readonly topStrip?: ReactNode;
    // Left zone: the NavRail. AppShell only seats it in the left column.
    readonly nav: ReactNode;
    // The thin material toolbar, seated above the material.
    readonly toolbar: ReactNode;
    // The centre — the protagonist. Always seated in the centre column;
    // never moved.
    readonly material: ReactNode;
    // The right-zone content (the SecondaryPane). Mounted whenever the dock is
    // present (collapsed OR open) so summon/dismiss ANIMATES rather than
    // teleports; AppShell (not the caller) gates it on `dock` — pass the raw
    // furniture. CSS collapses it to zero width while `dock` is `collapsed`.
    readonly secondary: ReactNode;
    // The right-edge dock ribbon (the switch + badge home). Shows whenever the
    // dock is present (the editor), collapsed or open — a thin column at the far
    // right edge, so the Quality badge stays glanceable while the body is
    // collapsed (right-dock-design.md §2). AppShell gates it on `dock`; pass the
    // raw node. Optional (nav-only stories omit it); when absent, no ribbon
    // column is seated.
    readonly dockRibbon?: ReactNode;

    // Nav re-summon affordance, revealed by CSS ONLY at the narrow breakpoint
    // when the rail is tucked off-canvas (data-nav="hidden") — restores doc-02's
    // off-canvas summon so a scrim-dismissed rail is recoverable.
    readonly navSummon?: ReactNode;

    // Author-chosen widths (px) for the expanded rail + the open dock, from the
    // persisted panel layout. Applied as CSS vars; absent → the token defaults.
    readonly railWidth?: number;
    readonly dockWidth?: number;
    // The draggable/keyboard resize handles (PanelResizeHandle), seated on the
    // rail's right edge + the dock's left edge. `railSash` is caller-gated (omit
    // when the rail isn't resizable). `dockSash` is gated by AppShell on `dock`
    // — it mounts whenever the dock is `present` (collapsed OR open, same gate
    // as the secondary body) so it can CSS-transition glued to the panel's own
    // open/close animation; AppShell.css keeps it non-interactive while
    // collapsed, so a handle still can never be USED without an open panel.
    // Pass the raw node (or omit in stories).
    readonly railSash?: ReactNode;
    readonly dockSash?: ReactNode;

    // The drag-to-EXPAND handles, seated on a COLLAPSED panel's inner edge (the
    // dock's left edge / the rail's right edge) so a collapsed panel can be
    // PULLED open, not only clicked open (owner request). `dockOpenHandle` shows
    // only while the dock is `collapsed` (present, no slot) — once open the
    // resize `dockSash` is the edge affordance instead; `railOpenHandle` shows
    // only while `navState` is `collapsed`. Pass the raw node (or omit).
    readonly dockOpenHandle?: ReactNode;
    readonly railOpenHandle?: ReactNode;
    // The LIVE preview width (px) while a drag-to-expand gesture is in flight —
    // the collapsed panel tracks the pointer 1:1 at this width. `undefined` when
    // no such gesture is active (the resting collapsed/open widths apply). Set
    // it and AppShell flips the panel visible at this width with transitions
    // suppressed (instant tracking — the feel-fast invariant).
    readonly dockDragWidth?: number;
    readonly railDragWidth?: number;

    // === Scrim dismissal (narrow viewports; CSS decides visibility) ===
    // Tap on the secondary overlay's scrim — the shell closes the pane.
    readonly onSecondaryScrimClick?: () => void;

    readonly className?: string;
};

// Project the one dock fact into the three render decisions, exhaustively — the
// panel body, the ribbon, and the handle are all read off THIS single value, so
// adding a DockState variant forces this table to be updated (exhaustSwitchCase)
// rather than silently defaulting a part on/off.
type DockRender = {
    // Editor-scoped: the ribbon column shows and the body mounts (collapsed or
    // expanded). False → the dock is gone entirely.
    readonly present: boolean;
    // A slot is summoned: the panel is expanded AND the resize handle mounts.
    // The handle's existence is exactly this bit — atomic with the panel.
    readonly open: boolean;
    // The summoned slot, or null when none (drives the CSS slot attribute).
    readonly slot: OpenSecondary;
};
const projectDock = (dock: DockState): DockRender => {
    switch (dock.type) {
        case "gone":
            return { present: false, open: false, slot: null };
        case "collapsed":
            return { present: true, open: false, slot: null };
        case "open":
            return { present: true, open: true, slot: dock.slot };
        default:
            return exhaustSwitchCase(dock);
    }
};

export const AppShell = ({
    dock,
    navState,
    surface,
    topStrip,
    nav,
    toolbar,
    material,
    secondary,
    dockRibbon,
    navSummon,
    railWidth,
    dockWidth,
    railSash,
    dockSash,
    dockOpenHandle,
    railOpenHandle,
    dockDragWidth,
    railDragWidth,
    onSecondaryScrimClick,
    className,
}: AppShellProps) => {
    // The three dock parts read off ONE fact. The handle now shares the body's
    // `dockRender.present` mount gate (so it can track the body's own open/close
    // transition in CSS instead of snapping to its final spot); AppShell.css is
    // what keeps it non-interactive while merely `present` (not `open`), so it
    // still can't be USED without an open panel. All three vanish together with
    // the ribbon and body when `dockRender.present` is false.
    const dockRender = projectDock(dock);
    const secondaryOpen = dockRender.open;

    // The author's persisted widths as CSS custom properties (the zone rules
    // read them, falling back to the token defaults when unset). Typed as
    // CSSProperties + a `--*` index so setting custom properties needs no cast.
    const shellVars: CSSProperties & Record<`--${string}`, string> = {};
    if (railWidth !== undefined) shellVars["--rail-width"] = `${railWidth}px`;
    if (dockWidth !== undefined) shellVars["--dock-width"] = `${dockWidth}px`;
    // A live drag-to-expand gesture publishes its preview width as a CSS var and
    // arms the matching `data-*-drag` attribute below, so the collapsed panel
    // reveals at this width with transitions off (1:1 pointer tracking).
    if (dockDragWidth !== undefined)
        shellVars["--dock-drag-width"] = `${dockDragWidth}px`;
    if (railDragWidth !== undefined)
        shellVars["--rail-drag-width"] = `${railDragWidth}px`;

    // The shell stacks the top strip over the three-zone grid. The grid keeps
    // ALL its geometry (data-attrs, container queries, scrims) — the strip is a
    // separate horizontal band above it, so nothing in the grid changes.
    return (
        <div className="bilda-app-shell-root">
            {topStrip != null && (
                <div className="bilda-app-shell__top-strip">{topStrip}</div>
            )}
            <div
                className={`bilda-app-shell${className ? ` ${className}` : ""}`}
                data-nav={navState}
                data-surface={surface.type}
                data-secondary={secondaryOpen ? "open" : "closed"}
                data-secondary-slot={dockRender.slot ?? "none"}
                data-dock-drag={
                    dockDragWidth !== undefined ? "opening" : "idle"
                }
                data-rail-drag={
                    railDragWidth !== undefined ? "opening" : "idle"
                }
                style={shellVars}
            >
                {/* Left zone — the nav rail. Its own <nav> landmark lives in the
                NavRail; AppShell only seats it. */}
                <div className="bilda-app-shell__nav">{nav}</div>

                {/* Resize handles — thin overlays centred on the rail's right
                edge / the dock's left edge, positioned by the width vars. Only
                rendered when the panel is resizable (host-gated). */}
                {railSash != null && (
                    <div className="bilda-app-shell__rail-sash">{railSash}</div>
                )}
                {/* Rail drag-to-expand handle — the collapsed rail's inner
                (right) edge, present ONLY while the rail is `collapsed` (the icon
                rail); once expanded the resize `railSash` is that edge instead.
                CSS reveals it at the wide breakpoint only (hidden where the rail
                floats as an overlay). */}
                {navState === "collapsed" && railOpenHandle != null && (
                    <div className="bilda-app-shell__rail-open-handle">
                        {railOpenHandle}
                    </div>
                )}
                {/* Dock resize handle — mounts whenever the dock is `present`
                (collapsed OR open), the SAME gate the secondary body already
                uses below, so it can CSS-transition in step with the panel's
                own width animation instead of snapping to its final spot the
                instant `open` flips (owner-reported floating-handle bug).
                AppShell.css keeps it functionally absent while collapsed
                (visibility:hidden, not focusable/tab-reachable) — the
                "no handle without an OPEN panel" guarantee still holds, now
                enforced by CSS rather than by mount/unmount. */}
                {dockRender.present && dockSash != null && (
                    <div className="bilda-app-shell__dock-sash">{dockSash}</div>
                )}
                {/* Dock drag-to-expand handle — the collapsed dock's inner
                (left) edge, present ONLY while the dock is `collapsed` (present,
                no slot summoned); once a slot is open the resize `dockSash` is
                that edge instead, so the two never coexist. Grab it and pull
                inward to open (owner request). CSS reveals it at the wide
                breakpoint only (hidden where the dock floats as an overlay). */}
                {dockRender.present &&
                    !dockRender.open &&
                    dockOpenHandle != null && (
                        <div className="bilda-app-shell__dock-open-handle">
                            {dockOpenHandle}
                        </div>
                    )}

                {/* Nav re-summon — CSS reveals it ONLY at the narrow breakpoint when
                the rail is tucked off-canvas (data-nav="hidden"), so a
                scrim-dismissed rail can be brought back. Inert otherwise. */}
                <div className="bilda-app-shell__nav-summon">{navSummon}</div>

                {/* Centre column — toolbar / material, in that order. The
                material is <main>; it never leaves this column. It is a PURE
                EDITOR at max canvas — the Assistant is the genie float (a fixed
                overlay the host renders), never a foot or a grid region here. */}
                <div className="bilda-app-shell__centre">
                    {toolbar != null && (
                        <div className="bilda-app-shell__toolbar">
                            {toolbar}
                        </div>
                    )}
                    <main className="bilda-app-shell__material">
                        {material}
                    </main>
                </div>

                {/* Secondary scrim — presentational; mirrors the nav scrim. Always
                mounted; CSS reveals it only when the right zone floats as a
                narrow overlay (≤832px) AND is open. Pointer-dismiss only — the
                keyboard path is Esc / the pane's close control, so it stays out
                of the a11y tree (role="presentation", not focusable). */}
                {/* biome-ignore lint/a11y/noStaticElementInteractions: presentational scrim; role="presentation" + pointer-only dismiss (keyboard path is Esc / the pane's close control) */}
                <div
                    role="presentation"
                    className="bilda-app-shell__scrim bilda-app-shell__scrim--secondary"
                    onClick={onSecondaryScrimClick}
                />

                {/* Right zone — the single summonable secondary. ALWAYS mounted so
                summon/dismiss ANIMATES (width + opacity) instead of teleporting;
                `data-secondary` on the root drives it open/closed. When closed
                it collapses to zero width AND flips to visibility:hidden, which
                removes it — and any focusable slot content — from the tab order
                and the a11y tree, so no phantom focusable region is left behind.
                Below ~832px CSS floats it as an overlay drawer over the scrim. */}
                <div className="bilda-app-shell__secondary">
                    {dockRender.present ? secondary : null}
                </div>

                {/* Right-edge dock ribbon — a thin column (the switch + badge
                home), PERSISTENT while the dock is `present` (the editor). It
                and the body share `dockRender.present`, so both vanish together
                off a material editor — the ribbon can never outlive its body. */}
                {dockRender.present && dockRibbon != null && (
                    <div className="bilda-app-shell__dock-ribbon">
                        {dockRibbon}
                    </div>
                )}
            </div>
        </div>
    );
};
